import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SceneOptionsMenuSpeakers from './SceneOptionsMenu/SceneOptionsMenuSpeakers/SceneOptionsMenuSpeakers';
import SceneOptionsMenuLayout from './SceneOptionsMenu/SceneOptionsMenuLayout/SceneOptionsMenuLayout';
import SceneOptionsMenuDelete from './SceneOptionsMenu/SceneOptionsMenuDelete/SceneOptionsMenuDelete';
import SceneThumbnail from './SceneThumbnail';
import { MIN_SCENE_WIDTH } from './TimelineScenesConstants';
import { increaseZoomLevel } from '../../store/zoomLevel';
import { getDeletesForSegment } from '../../playerConfigUtil';
import { MINIMUM_SCENE_TIME_DIFFERENCE } from '@/player-config/src/constants/scene.constants';
import { getStartForSegment } from '@/player-config/src/utils/clipUtils';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { Scene, Scenes } from '@/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { CompositeSegment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { ClipMetadata } from '@/player-config/src/types/clip-types';
import { SizeType } from '@/player-config/src/types/asset-types';
import { Sequence } from '@/player-config/src/types/RemotionPlayerConfigTypes/Sequence';
import useDialog from '@/components/organisms/useDialog';
import { convertSecondsToTimeString } from '@/player-config/src/utils/timeUtils';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { getFlattenedDeletes } from '@/player-config/src/generators/compositeSegment/utils';
import { useRemotionPlayerPause } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerPause';
import { isAudioContent } from '@/stores/core';
import { useSceneThumbnail } from '@/Pages/TranscriptPage/useSceneThumbnail';

interface TimelineSceneProps {
  scene: Scene;
  // TODO: @AshwinBhatkal - to be removed with - 91dag8e1yg8a678e29
  scenes: Scenes;
  canDeleteScene: boolean;
  compositeSegment: CompositeSegment;
  videoSize: SizeType;
  showBorder: boolean;
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>, chapterId: string, maxOffset: number, minOffset: number) => void;
  previousScene: Scene | null;
  nextScene: Scene | null;
  isDraggingCurrentScene: boolean;
  isDraggingSomeScene: boolean;
  compositeSegmentDragStart: number;
  playerConfig: ClipMetadata['player_config'];
  compositeSegmentDeleteBounds: Sequence[];
  onMenuStateChange: (isOpen: boolean) => void;
  onZoomToTimeRange?: (startTime: number, endTime: number) => void;
  compositionHeight: number;
  compositionWidth: number;
  fps: number;
}

function TimelineScene({
  scene,
  scenes,
  canDeleteScene,
  compositeSegment,
  videoSize,
  onMouseDown,
  previousScene,
  nextScene,
  isDraggingCurrentScene,
  isDraggingSomeScene,
  compositeSegmentDragStart,
  playerConfig,
  onMenuStateChange,
  compositeSegmentDeleteBounds,
  compositionHeight,
  compositionWidth,
  fps
}: TimelineSceneProps) {
  const isFirstScene = !previousScene;
  const isLastScene = !nextScene;
  const sceneWidth = scene.toFrame - scene.fromFrame;
  const sceneStart = scene.fromFrame;
  const sceneRef = useRef<HTMLDivElement>(null);
  const scenePopoverRef = useRef<HTMLDivElement>(null);
  const scenePopoverButtonRef = useRef<HTMLButtonElement>(null);
  const [computedCurrentSceneWidth, setComputedCurrentSceneWidth] = useState(0);
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);
  const { seekToFrame } = useRemotionPlayerSeek();
  const { pause } = useRemotionPlayerPause();

  const { clipData } = useClipsContext();

  const { openDialog, closeDialog, isOpen: isSceneMenuOpen } = useDialog();

  const shouldShowZoomOnScene = useMemo(
    () => computedCurrentSceneWidth <= MIN_SCENE_WIDTH,
    [computedCurrentSceneWidth]
  );

  const shouldShowZoomOnGrab = useMemo(() => {
    if (shouldShowZoomOnScene) {
      return true;
    }

    if (isFirstScene) {
      return false;
    }

    if (!previousScene) {
      return false;
    }

    const previousSceneWidth = previousScene.toFrame - previousScene.fromFrame;
    const computedPreviousSceneWidth = previousSceneWidth * (computedCurrentSceneWidth / sceneWidth);

    if (computedPreviousSceneWidth <= MIN_SCENE_WIDTH) {
      return true;
    }

    return false;
  }, [computedCurrentSceneWidth, isFirstScene, previousScene, sceneWidth, shouldShowZoomOnScene]);

  useEffect(() => {
    onMenuStateChange(isSceneMenuOpen);
  }, [isSceneMenuOpen, onMenuStateChange]);

  useEffect(() => {
    if (isSceneMenuOpen) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && isSceneMenuOpen) {
          closeDialog();
          EventBus.dispatch(CustomEvents.SceneMenuStateChanged, { isSceneMenuOpen: false });
        }
      };

      const handleClickOutside = (event: MouseEvent) => {
        if (scenePopoverRef.current && !scenePopoverRef.current.contains(event.target as Node)) {
          closeDialog();
          EventBus.dispatch(CustomEvents.SceneMenuStateChanged, { isSceneMenuOpen: false });
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [closeDialog, isSceneMenuOpen]);

  const flattenedDeletes = useMemo(() => {
    return getFlattenedDeletes({ clipDeletes: getDeletesForSegment(clipData, compositeSegment.id), fps });
  }, [clipData, fps, compositeSegment.id]);

  // !! TODO @@HiteshChoudhary0098 this function's logic should be simplified to choose which frame to show.
  const thumbnailFrameToDisplay = useMemo(() => {
    const unchangedScene = scenes.find(a => a.id === scene.id);

    if (!unchangedScene) {
      return 0;
    }

    if (scene.fromFrame === 0) {
      return Math.ceil(Math.max(compositeSegmentDragStart * fps, 0));
    }

    const deletesBeforeCurrentScene = flattenedDeletes
      .filter(currDelete => currDelete.fromFrame < unchangedScene.fromFrame)
      .map(currDelete => ({ ...currDelete, toFrame: Math.min(currDelete.toFrame, unchangedScene.fromFrame) }));

    const framesToRemove = deletesBeforeCurrentScene
      .map(bound => bound.toFrame - bound.fromFrame)
      .reduce((a, b) => a + b, 0);

    return Math.max(0, Math.ceil(unchangedScene.fromFrame - framesToRemove));
  }, [scenes, scene.fromFrame, scene.id, flattenedDeletes, compositeSegmentDragStart, fps]);

  const thumbnailFrameToSeek = useMemo(() => {
    const unchangedScene = scenes.find(a => a.id === scene.id);

    if (!unchangedScene) {
      return 0;
    }

    const deletesTillSceneFromFrame = compositeSegmentDeleteBounds
      .filter(
        // This a.toFrame < pureScene.toFrame will always show the thumbnail for after the deleted section
        a => a.fromFrame !== 0 && a.toFrame < unchangedScene.toFrame
      )
      .reduce((acc, curr) => acc + (curr.toFrame - curr.fromFrame), 0);

    return Math.max(
      0,
      Math.ceil(
        scene.fromFrame -
          deletesTillSceneFromFrame +
          compositeSegment.fromFrame +
          Math.max(compositeSegmentDragStart * fps, 0) +
          // This plus one is to account for the scenes overlapping each other
          1
      )
    );
  }, [scene, compositeSegment, compositeSegmentDragStart, compositeSegmentDeleteBounds, scenes, fps]);

  const onSceneClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (shouldShowZoomOnScene) {
        return;
      }

      const isNotAudio = !isAudioContent();
      if (isNotAudio) {
        openDialog();
      }

      EventBus.dispatch(CustomEvents.SceneMenuStateChanged, { isSceneMenuOpen: isNotAudio });

      pause();
      setX(e.clientX);
      setY(scenePopoverButtonRef.current?.getBoundingClientRect().top ?? 0);
    },
    [openDialog, shouldShowZoomOnScene, pause]
  );

  const onHandleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (shouldShowZoomOnGrab) {
        return;
      }
      const maxOffset =
        (nextScene ? nextScene.fromFrame - MINIMUM_SCENE_TIME_DIFFERENCE * fps : scene.toFrame) - scene.fromFrame;
      const minOffset = previousScene
        ? scene.fromFrame - previousScene.fromFrame - MINIMUM_SCENE_TIME_DIFFERENCE * fps
        : scene.fromFrame;
      onMouseDown(e, scene.id, maxOffset / fps, -minOffset / fps);
    },
    [nextScene, onMouseDown, previousScene, scene.fromFrame, scene.id, scene.toFrame, shouldShowZoomOnGrab, fps]
  );

  useEffect(() => {
    if (!sceneRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setComputedCurrentSceneWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(sceneRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const onSceneContainerClick = useCallback(() => {
    if (shouldShowZoomOnScene || shouldShowZoomOnGrab) {
      increaseZoomLevel();
      setTimeout(() => {
        sceneRef.current?.scrollIntoView({ inline: 'center' });
        seekToFrame(thumbnailFrameToSeek);
      }, 200);
    }
  }, [shouldShowZoomOnScene, shouldShowZoomOnGrab, seekToFrame, thumbnailFrameToSeek]);

  const { croppedImageUrl } = useSceneThumbnail({
    clip: clipData,
    startTimeInSeconds:
      getStartForSegment({
        clip: clipData,
        segmentId: compositeSegment.id,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      }) +
      scene.fromFrame / fps
  });

  return (
    <div
      ref={sceneRef}
      className={`${isLastScene ? 'pr-[5px]' : 'pr-[1.5px]'} ${
        isFirstScene ? 'pl-[5px]' : 'pl-[1.5px]'
      } absolute h-full rounded-md ${shouldShowZoomOnScene ? 'cursor-zoom-in' : ''}`}
      style={{
        minWidth: `calc(${sceneWidth}px * var(--timeline-zoom))`,
        width: `calc(${sceneWidth}px * var(--timeline-zoom))`,
        left: `calc(${sceneStart}px * var(--timeline-zoom))`
      }}
      onClick={onSceneContainerClick}
    >
      <div className="h-full max-w-full overflow-hidden rounded-[3px]">
        <button
          className={`relative size-full overflow-hidden truncate bg-slate-200 px-0.5 py-1 transition-colors duration-200 ${
            isDraggingSomeScene ? '' : 'hover:bg-slate-400'
          } ${shouldShowZoomOnScene ? 'cursor-zoom-in' : 'cursor-pointer'}`}
          onClick={onSceneClick}
          ref={scenePopoverButtonRef}
        >
          <SceneThumbnail
            playerConfig={playerConfig}
            videoSize={videoSize}
            thumbnailFrameToDisplay={thumbnailFrameToDisplay}
            compositeSegment={compositeSegment}
            compositionHeight={compositionHeight}
            compositionWidth={compositionWidth}
            mediaContent={clipData.content}
            croppedImageUrl={croppedImageUrl}
            showBackgroundBlur
          />
        </button>
      </div>
      {!isFirstScene && (
        <div
          className={`absolute inset-y-[3px] left-[-1.5px] z-10 w-[3px] opacity-0 hover:opacity-100 ${
            shouldShowZoomOnGrab ? 'cursor-zoom-in' : 'cursor-col-resize'
          }`}
          onMouseDown={onHandleMouseDown}
        >
          <div className="m-auto h-full w-px bg-slate-600"></div>
        </div>
      )}
      {isSceneMenuOpen && (
        <div
          className="fixed inset-0 z-[70]"
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onMouseMove={e => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <div
            className="fixed z-[100] w-48 origin-bottom-right -translate-x-1/2 -translate-y-full rounded-md bg-white p-1 shadow-lg ring-1 ring-black/5 focus:outline-none"
            style={{ left: x, top: y }}
            ref={scenePopoverRef}
          >
            <SceneOptionsMenuLayout
              sceneId={scene.id}
              scenes={scenes}
              clip={clipData}
              compositeSegmentId={compositeSegment.id}
            />
            <SceneOptionsMenuSpeakers sceneId={scene.id} scenes={scenes} compositeSegmentId={compositeSegment.id} />
            {canDeleteScene && <SceneOptionsMenuDelete sceneId={scene.id} compositeId={compositeSegment.id} />}
          </div>
        </div>
      )}
      {isDraggingCurrentScene && (
        <div className="absolute -bottom-1 z-20 h-[21px] w-fit -translate-x-1/2 translate-y-full rounded-sm border border-zinc-900 bg-zinc-900 px-1.5 py-0.5 text-2xs leading-[15px] text-white">
          {convertSecondsToTimeString(scene.fromFrame / fps, 'HH:mm:ss:S')}
        </div>
      )}
    </div>
  );
}

export default memo(TimelineScene);
